import {
  IDocumentAssemblerTemplateCategory,
  useDocumentAssemblerTemplateCategories,
} from '@commenda/api';
import { Button, Dropdown, Icons, useToggle } from '@commenda/kakashi';
import { pluralize } from '@commenda/utils';
import { useMemo, useState } from 'react';
import { DocumentAssemblerCategoryCreateDialog } from './DocumentAssemblerCategoryCreateDialog';

import { DocumentAssemblerCategoryEditDialog } from './DocumentAssemblerCategoryEditDialog';

interface DocumentAssemblerCategoriesMenuProps {
  /** Called after any category changes, so the templates list can refresh its nested categories. */
  onCategoriesChanged?: () => void;
  enabled?: boolean;
  /** A category is global across every customer, so only Commenda staff may add one. */
  canCreate?: boolean;
}

export const DocumentAssemblerCategoriesMenu = ({
  onCategoriesChanged,
  enabled = true,
  canCreate = false,
}: DocumentAssemblerCategoriesMenuProps) => {
  const createCategoryModalVisibility = useToggle(false);
  const [categoryToEdit, setCategoryToEdit] =
    useState<IDocumentAssemblerTemplateCategory | null>(null);

  const { templateCategories, mutate } = useDocumentAssemblerTemplateCategories(
    {
      enabled,
      includeArchived: true,
    }
  );

  const categories = templateCategories ?? [];

  // Split rather than suffixed: an archived category cannot be edited and cannot take a new
  // template, so listing it beside the live ones invites a click that only leads to a refusal.
  const liveCategories = useMemo(
    () => categories.filter((category) => !category.archivedAt),
    [categories]
  );
  const archivedCategories = useMemo(
    () => categories.filter((category) => category.archivedAt),
    [categories]
  );

  const refetchCategoriesAndNotify = async () => {
    await mutate();
    onCategoriesChanged?.();
  };

  return (
    <>
      <Dropdown
        align="end"
        menu={{
          groups: [
            ...(liveCategories.length
              ? [
                  {
                    label: 'Categories',
                    items: liveCategories.map((category) => ({
                      label: `${category.name} (${pluralize(category.templateCount, 'template')})`,
                      icon: Icons.Edit03,
                      onClick: () => setCategoryToEdit(category),
                    })),
                  },
                ]
              : []),
            ...(canCreate
              ? [
                  {
                    items: [
                      {
                        label: 'Add a category',
                        icon: Icons.Plus,
                        onClick: createCategoryModalVisibility.on,
                      },
                    ],
                  },
                ]
              : []),
            ...(archivedCategories.length
              ? [
                  {
                    label: 'Archived',
                    items: archivedCategories.map((category) => ({
                      label: category.name,
                      icon: Icons.Archive,
                      onClick: () => setCategoryToEdit(category),
                    })),
                  },
                ]
              : []),
          ],
        }}
      >
        <Button variant="secondary" size="sm">
          Categories
          <Icons.ChevronDown className="size-4" />
        </Button>
      </Dropdown>

      {canCreate && createCategoryModalVisibility.value ? (
        <DocumentAssemblerCategoryCreateDialog
          onClose={createCategoryModalVisibility.off}
          onCreated={refetchCategoriesAndNotify}
        />
      ) : null}

      {categoryToEdit ? (
        <DocumentAssemblerCategoryEditDialog
          category={categoryToEdit}
          onClose={() => setCategoryToEdit(null)}
          onChanged={refetchCategoriesAndNotify}
        />
      ) : null}
    </>
  );
};
